import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { deviceSettingsQuery, deviceSummaryQuery, deviceTabsQuery, nestedTableQuery, post } from '../api/protectee'
import { ApiError } from '../api/client'
import { dayMonthYear, escapeHtml, stamp } from '../inventoree/format'
import { Spinner } from '../ui/Spinner'
import { toast } from '../ui/notify'
import { NestedTable, type NestedColumn, type NestedState } from '../securitee/NestedTable'
import { BackupTab } from './BackupTab'
import { LocationMap } from './LocationMap'
import { StolenReports } from './StolenReports'
import { SettingsCheck } from '../ui/SettingsParts'

/**
 * Randul desfasurat al unei masini din Protectee - `Protectee::getActionTabs`
 * (devices/others/actionTabs.php): Summary, Location history, Events,
 * Settings, Backup settings, Backup events.
 */
export function ProtecteeDeviceTabs({ id }: { id: number }) {
  const tabs = useQuery(deviceTabsQuery(id))
  const [tab, setTab] = useState('summary')
  if (tabs.error) return <div className="alert alert-danger" role="alert">Something went wrong: {tabs.error.message}</div>
  if (!tabs.data) return <Spinner />
  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          {tabs.data.tabs.map((t) => (
            <li key={t.key}>
              <a href="#" className={`action_button${tab === t.key ? ' active' : ''}`} onClick={(e) => { e.preventDefault(); setTab(t.key) }}>
                {t.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {tab === 'summary' && <SummaryTab id={id} />}
          {tab === 'locations' && (
            <>
              <LocationMap id={id} />
              <TableTab id={id} kind="locations" tableId="deviceLocations" columns={LOCATION_COLUMNS} />
            </>
          )}
          {tab === 'reports' && <StolenReports id={id} />}
          {tab === 'events' && <TableTab id={id} kind="events" tableId="deviceEvents" columns={EVENT_COLUMNS} />}
          {tab === 'settings' && <SettingsTab id={id} />}
          {tab === 'backup' && <BackupTab id={id} />}
          {tab === 'backup_events' && <TableTab id={id} kind="backup-events" tableId="deviceBackupEvents" columns={BACKUP_COLUMNS} />}
        </div>
      </div>
    </div>
  )
}

function Stat({ color, icon, number, label }: { color: string; icon: string; number: string; label: string }) {
  return (
    <div className="col-md-3">
      <div className={`dashboard-stat ${color}`}>
        <div className="visual"><i className={icon}></i></div>
        <div className="details"><div className="number">{number}</div><div className="desc"></div></div>
        <div className="more">{label}</div>
      </div>
    </div>
  )
}

/**
 * Legatura spre Google Maps pentru coordonatele masinii, sau null.
 *
 * Portalul vechi incarca harta Google in pagina, cu o cheie scrisa in
 * macheta (`devices/others/summary.php`). Aici NU se incarca nimic de la
 * Google: o harta pusa in pagina ar trimite cereri de pe statiile bancii la
 * fiecare deschidere a fisei. Ramane o legatura, pe care omul o deschide
 * cand vrea, intr-o fila noua. Fara cheie, fara script.
 */
export function mapsUrl(latitude: string | null, longitude: string | null): string | null {
  const number = /^\s*-?\d+(\.\d+)?\s*$/
  if (!number.test(latitude ?? '') || !number.test(longitude ?? '')) return null
  const lat = Number(latitude)
  const lng = Number(longitude)
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null
  return `https://www.google.com/maps?q=${lat},${lng}`
}

/** devices/others/summary.php */
function SummaryTab({ id }: { id: number }) {
  const q = useQuery(deviceSummaryQuery(id))
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  const s = q.data
  const l = s.location
  const na = (v: string | null) => (v ? v : <em>n/a</em>)
  const map = mapsUrl(l.latitude, l.longitude)
  const [day, hour] = (s.last_backup?.date ?? '').split(' ')
  return (
    <>
      <div className="row">
        <Stat color="blue-madison" icon="fas fa-shield-alt" number={s.status} label="Status" />
        <Stat color="blue-madison" icon="fa fa-map-marker" number={`${l.city ?? 'n/a'} ${l.country ?? ''}`.trim()} label="Location" />
        <Stat color="blue-madison" icon="fa fa-calendar" number={s.last_backup ? `${day ?? ''} ${hour ?? ''}`.trim() : 'n/a'} label="Last Backup" />
        <Stat color="blue-madison" icon="fas fa-folder-open" number={s.last_backup ? s.last_backup.status : 'n/a'} label="Last Backup Response" />
      </div>
      <div className="row">
        <div className="col-md-12">
          <table style={{ width: '100%' }} className="details-table">
            <tbody>
              <tr><td colSpan={2}><h4 className="bordered"><strong>Device</strong> details</h4></td></tr>
              <tr><td className="title">Last seen:</td><td>{na(l.date)}</td></tr>
              <tr><td className="title">User logged on:</td><td><i className="fa fa-user"></i> {na(l.logged_user)}</td></tr>
              <tr><td className="title">Last external IP:</td><td>{na(l.external_ip)}</td></tr>
              <tr><td className="title">Last local IP:</td><td>{na(l.internal_ip)}</td></tr>
              <tr><td className="title">Address:</td><td><i className="fa fa-building"></i> {na(l.address)}</td></tr>
              <tr><td className="title">City:</td><td><i className="far fa-map"></i> {na(l.city)}</td></tr>
              <tr><td className="title">Country:</td><td>{na(l.country)}</td></tr>
              <tr>
                <td className="title">Coordinates:</td>
                <td>
                  {l.latitude && l.longitude ? `${l.latitude}, ${l.longitude}` : <em>n/a</em>}
                  {map ? (
                    <>
                      {' '}
                      <a className="protectee-map-link" href={map} target="_blank" rel="noopener noreferrer">
                        <i className="fa fa-external-link-alt" aria-hidden="true"></i> Open in Google Maps
                      </a>
                    </>
                  ) : null}
                </td>
              </tr>
              <tr><td className="title">ISP:</td><td>{na(l.isp)}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

/** moment(x).format('D MMM YYYY, HH:mm') - coloana Date din cele trei tabele (protectee.js). */
export function eventDate(value: unknown): string {
  const when = stamp(value)
  if (!when) return escapeHtml(value)
  return `${dayMonthYear(when)}, ${String(when.getHours()).padStart(2, '0')}:${String(when.getMinutes()).padStart(2, '0')}`
}

const DASH = '<i class="fas fa-minus font-10"></i>'
const orDash = (v: unknown) => (v === null || v === undefined || v === '' ? DASH : escapeHtml(v))

/** `deviceBackupEventsModel`: tipul algoritmului cu numele lui, nu cu cifra. */
const ALGORITHM_NAMES: Record<string, string> = { '0': 'Only once', '1': 'By day', '2': 'By week', '3': 'By month', '4': 'GFS', '5': 'Custom' }

const LOCATION_COLUMNS: NestedColumn[] = [
  { key: 'date', title: 'Date', titleHtml: '<i class="far fa-calendar"></i> Date', html: (r) => eventDate(r.date) },
  { key: 'external_ip', title: 'External IP', html: (r) => escapeHtml(r.external_ip) },
  { key: 'latitude', title: 'Latitude', html: (r) => escapeHtml(r.latitude) },
  { key: 'longitude', title: 'Longitude', html: (r) => escapeHtml(r.longitude) },
  { key: 'address', title: 'Address', html: (r) => orDash(r.address) },
  { key: 'city', title: 'City', html: (r) => orDash(r.city) },
  { key: 'country', title: 'Country', html: (r) => orDash(r.country) },
]

const EVENT_COLUMNS: NestedColumn[] = [
  { key: 'date', title: 'Date', titleHtml: '<i class="far fa-calendar"></i> Date', html: (r) => eventDate(r.date) },
  /* Mesajul vine din jurnal cu <b>, ca in macheta veche. */
  { key: 'message', title: 'Message', html: (r) => String(r.message ?? '') },
  { key: 'Prenume', title: 'User', html: (r) => (r.Prenume ? escapeHtml(`${r.Prenume} ${r.Nume ?? ''}`) : '<strong>System</strong>') },
]

export const BACKUP_COLUMNS: NestedColumn[] = [
  { key: 'response_date', title: 'Date', titleHtml: '<i class="far fa-calendar"></i> Date', html: (r) => eventDate(r.response_date) },
  { key: 'backup_id', title: 'Backup name', html: (r) => escapeHtml(r.backup_name) || escapeHtml(r.backup_id) },
  { key: 'backup_type', title: 'Backup type', html: (r) => (Number(r.backup_type) === 0 ? 'Task' : 'Profile') },
  { key: 'algorithm', title: 'Algorithm', html: (r) => ALGORITHM_NAMES[String(r.algorithm ?? '')] ?? escapeHtml(r.algorithm) },
  { key: 'status', title: 'Status', html: (r) => escapeHtml(r.status) },
  { key: 'response', title: 'Response', html: (r) => escapeHtml(r.response) },
]

function TableTab({ id, kind, tableId, columns }: { id: number; kind: 'locations' | 'events' | 'backup-events'; tableId: string; columns: NestedColumn[] }) {
  const [state, setState] = useState<NestedState>({ start: 0, length: 10, order: { column: 0, dir: 'desc' }, search: '' })
  const col = columns[state.order.column] ?? columns[0]!
  const q = useQuery({
    ...nestedTableQuery(id, kind, { draw: 1, start: state.start, length: state.length, orderColumn: state.order.column, orderName: col.key, orderDir: state.order.dir, search: state.search }),
    placeholderData: keepPreviousData,
  })
  return (
    <div className="row">
      <div className="col-md-12">
        <NestedTable id={tableId} columns={columns} rows={q.data?.data ?? []} total={q.data?.recordsTotal ?? 0} fetching={q.isFetching} error={q.isError ? (q.error as Error).message : null} state={state} onState={setState} />
      </div>
    </div>
  )
}

/** devices/others/deviceSettings.php + stolenDeviceSettings.php */
function SettingsTab({ id }: { id: number }) {
  const client = useQueryClient()
  const q = useQuery(deviceSettingsQuery(id))
  const [busy, setBusy] = useState(false)
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  const s = q.data

  const send = async (path: string, fields: Record<string, string>) => {
    if (busy) return
    setBusy(true)
    try {
      const body = await post(path, fields)
      toast(body.message, body.status === 'OK' ? 'Success' : 'Error', body.status === 'OK' ? 'success' : 'error')
      void client.invalidateQueries({ queryKey: ['protectee'] })
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Something went wrong.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }
  const onActions = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const data = new FormData(e.currentTarget)
    const fields: Record<string, string> = { email: String(data.get('email') ?? ''), interval: String(data.get('interval') ?? '0') }
    for (const k of ['webcam', 'desktop', 'apps', 'send_email']) if (data.get(k)) fields[k] = '1'
    void send(`/protectee/devices/${id}/actions`, fields)
  }
  const stolen = s.device_status > 0
  const blocked = s.login_status > 0
  const label = { textAlign: 'left' as const }

  return (
    <div className="protectee-settings">
      <header className="panel-heading sm" data-color="theme-inverse"><h4><strong>Device</strong> status</h4></header>
      <form className="form-horizontal labelcustomize deviceStatus" method="post" action="/protectee/deviceStatus" onSubmit={(e) => { e.preventDefault(); void send(`/protectee/devices/${id}/status`, { action: stolen ? 'protected' : 'stolen' }) }}>
        <div className="form-group">
          <label style={label} className="control-label col-md-3"><span className="color">{stolen ? 'Mark device as found' : 'Mark device as stolen'}</span></label>
          <div className="col-md-9">
            <button type="submit" className={stolen ? 'btn btn-primary' : 'btn btn-theme'} disabled={busy}>
              <i className="fas fa-exclamation-triangle font-14 color-white"></i> {stolen ? 'Protected' : 'Stolen'}
            </button>
          </div>
        </div>
      </form>

      {stolen ? (
        <>
          <header className="panel-heading sm" data-color="theme-inverse"><h4><strong>Device</strong> actions</h4></header>
          <form className="form-horizontal labelcustomize deviceActions" method="post" action="/protectee/deviceActions" onSubmit={onActions}>
            <div className="form-group">
              <label style={label} className="control-label col-md-3"><span className="color">Email</span></label>
              <div className="col-md-9"><input className="form-control" type="email" name="email" defaultValue={s.actions?.email ?? ''} /></div>
            </div>
            <div className="form-group">
              <label style={label} className="control-label col-md-3"><span className="color">Refresh interval</span></label>
              <div className="col-md-9">
                <select name="interval" className="form-control" defaultValue={String(s.actions?.interval ?? 0)}>
                  {s.intervals.map((n) => <option key={n} value={n}>{n === 0 ? 'Select...' : `${n} minutes`}</option>)}
                </select>
              </div>
            </div>
            {([['webcam', 'Webcam screenshot', s.actions?.webcam], ['desktop', 'Desktop screenshot', s.actions?.desktop], ['apps', 'Foreground application list', s.actions?.apps], ['send_email', 'Send email alert', s.actions?.send_email]] as const).map(([name, text, on]) => (
              <div className="form-group" key={name}>
                <label style={label} className="control-label col-md-3"><span className="color">{text}</span></label>
                <div className="col-md-9"><SettingsCheck aria-label={text} name={name} value="1" defaultChecked={!!on} /></div>
              </div>
            ))}
            <div className="form-group"><div className="col-md-9 col-md-offset-3"><button type="submit" className="btn btn-theme" disabled={busy}>Save</button></div></div>
          </form>
        </>
      ) : null}

      <header className="panel-heading sm" data-color="theme-inverse"><h4><strong>Users</strong> login</h4></header>
      <form className="form-horizontal labelcustomize accountsLogin" method="post" action="/protectee/accountsLogin" onSubmit={(e) => { e.preventDefault(); void send(`/protectee/devices/${id}/accounts`, { action: blocked ? 'unblock' : 'block' }) }}>
        <div className="form-group">
          <label style={label} className="control-label col-md-3"><span className="color">{blocked ? 'Unblock all accounts login' : 'Block all accounts login'}</span></label>
          <div className="col-md-9">
            <button type="submit" className={blocked ? 'btn btn-primary' : 'btn btn-theme'} disabled={busy}>
              <i className="fas fa-exclamation-triangle font-14 color-white"></i> {blocked ? 'Unblock' : 'Block'}
            </button>
          </div>
        </div>
      </form>

      <header className="panel-heading sm" data-color="theme-inverse"><h4><strong>Messages</strong></h4></header>
      <form className="form-horizontal labelcustomize sendMessage" method="post" action="/protectee/sendMessage" onSubmit={(e) => { e.preventDefault(); const f = e.currentTarget; void send(`/protectee/devices/${id}/message`, { message: String(new FormData(f).get('message') ?? '') }).then(() => f.reset()) }}>
        <div className="form-group">
          <label style={label} className="control-label col-md-3"><span className="color">Message</span></label>
          <div className="col-md-9"><textarea style={{ maxWidth: '100%' }} className="form-control" name="message" required></textarea></div>
        </div>
        <div className="form-group"><div className="col-md-9 col-md-offset-3"><button type="submit" className="btn btn-theme" disabled={busy}>Send message</button></div></div>
      </form>
    </div>
  )
}
